import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { NecClassificationInterface } from '../../component/classification/classification.interface';
import { NecLearnCertInterface } from '../../component/learncert/learncert.interface';
import { Classification } from '../../model/attr/classification';
import { DefaultLearnCertConfigDTO } from '../../model/learn-cert/default-learn-cert-config/default-learn-cert-config-dto';
import { LearnCertClassDTO } from '../../model/learn-cert/learn-cert-class-dto';
import { LearnCertDisableConfig } from '../../model/learn-cert/learn-cert-disable-config/learn-cert-disable-config';
import { Dept } from '../../model/tenant/dept';
import { NecDefaultLearnCertConfigInterface } from '../learncert/default-learn-cert-config.interface';
import { NecLocalStorageService } from '../localStorage/local-storage.service';
import { NecDeptInterface } from '../tenant/dept.interface';
import { UserService } from '../user/user.service';
import { JsonUtil } from '../util/json.util';
import { DictData } from './dict-data';
import { DictDataParam } from './dict-data-param';
import { DictDataType } from './dict-data-type.enum';

@Injectable()
export class NecDictDataService {
  private prefix = 'dict-';

  constructor(
    private ls: NecLocalStorageService,
    private userService: UserService,
    // private categoryInterface: NecCategoryInterface,
    // private categoryConfigInterface: NecCategoryConfigInterface,
    private classificationInterface: NecClassificationInterface,
    private learnCertInterface: NecLearnCertInterface,
    private defaultLearnCertConfigInterface: NecDefaultLearnCertConfigInterface,
    private deptInterface: NecDeptInterface
  ) {}

  /**
   * è·åå­å¸æ°æ®
   */
  public getDictData(type: DictDataType, param?: DictDataParam): Observable<any> {
    switch (type) {
      case DictDataType.CLASSIFICATION:
        return this.getClassificationDict(this.prefix + DictDataType.CLASSIFICATION);
      case DictDataType.LEARN_CERT_CLASS:
        return this.getLearnCertDict(this.prefix + DictDataType.LEARN_CERT_CLASS);
      case DictDataType.LEARN_CERT_DEFAULT_CONFIG:
        return this.getDefaultLearnCertConfigDict(this.prefix + DictDataType.LEARN_CERT_DEFAULT_CONFIG, param);
      case DictDataType.LEARN_CERT_DISABLE_CONFIG:
        return this.getLearnCertDisableConfigDict(this.prefix + DictDataType.LEARN_CERT_DISABLE_CONFIG, param);
      case DictDataType.TENANT_DEPTS:
        return this.getTenantDepts(this.prefix + DictDataType.TENANT_DEPTS, param);
      case DictDataType.MY_DEFINED_DEPTS:
        return this.getDefinedDepts(this.prefix + DictDataType.MY_DEFINED_DEPTS, param);
      default:
        return null;
    }
  }

  /**
   * å é¤ææå­å¸æ°æ®
   */
  delAllDictData() {
    let keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      let key = localStorage.key(i);
      if (key.indexOf('-' + this.prefix) !== -1) {
        keys.push(key);
      }
    }
    keys.forEach(key => {
      localStorage.removeItem(key);
    });
  }

  /**
   * å é¤ææå­å¸æ°æ®
   */
  delDictData(type: DictDataType) {
    this.ls.del(this.prefix + type);
  }

  private getDict(key: string, classReference: new () => any): any {
    let dictStr = this.ls.get(key);
    if (!dictStr) {
      return undefined;
    }
    let dictData: DictData = JsonUtil.jsonConvert(dictStr, DictData);

    if (new Date().getTime() - dictData.localTime > DictData.maxTimeInterval || dictData.expired) {
      this.ls.del(key);
      return undefined;
    }
    return JsonUtil.jsonConvert(dictData.data, classReference);
  }

  private getClassificationDict(key: string): Observable<Classification[]> {
    let classifications = this.getDict(key, Classification);
    if (!classifications || classifications.length === 0) {
      let observable = this.classificationInterface.getClassification(this.userService.getTenantId());
      return observable.pipe(
        map((resp: Classification[]) => {
          this.ls.set(key, new DictData(resp));
          return resp;
        })
      );
    }
    if (classifications[0].resId.includes(this.userService.getTenantId())) {
      return Observable.create(observer => {
        observer.next(classifications);
        observer.complete();
      });
    }
    this.ls.del(key);
    return this.getClassificationDict(key);
  }

  private getLearnCertDict(key: string): Observable<LearnCertClassDTO[]> {
    let tenantId = this.userService.getTenantId();
    let learnCertClasses: LearnCertClassDTO[] = this.getDict(key, LearnCertClassDTO);
    if (!learnCertClasses || learnCertClasses.length === 0) {
      let observable = this.learnCertInterface.getLearnCertTypes(tenantId);
      return observable.pipe(
        map((resp: LearnCertClassDTO[]) => {
          this.ls.set(key, new DictData(resp));
          return resp;
        })
      );
    }
    if (learnCertClasses[0].learnCertClass.tenantId === this.userService.getTenantId()) {
      return Observable.create(observer => {
        observer.next(learnCertClasses);
        observer.complete();
      });
    }
    this.ls.del(key);
    return this.getLearnCertDict(key);
  }

  private getDefaultLearnCertConfigDict(key: string, param: DictDataParam): Observable<DefaultLearnCertConfigDTO> {
    if (!param) {
      return null;
    }
    let configDto: DefaultLearnCertConfigDTO = this.getDict(key, DefaultLearnCertConfigDTO);
    if (!configDto) {
      let observable;
      if (param.isDept()) {
        observable = this.defaultLearnCertConfigInterface.queryDefaultConfigsByDeptId(param.searchId);
      } else if (param.isGroup()) {
        observable = this.defaultLearnCertConfigInterface.queryDefaultConfigsByGroupId(param.searchId);
      }
      return observable
        ? observable.pipe(
            map((resp: DefaultLearnCertConfigDTO) => {
              this.ls.set(key, new DictData(resp));
              return resp;
            })
          )
        : null;
    }
    if (configDto.findTenantId() === this.userService.getTenantId()) {
      return Observable.create(observer => {
        observer.next(configDto);
        observer.complete();
      });
    }
    this.ls.del(key);
    return this.getDefaultLearnCertConfigDict(key, param);
  }

  private getLearnCertDisableConfigDict(key: string, param: DictDataParam): Observable<LearnCertDisableConfig> {
    if (!param) {
      return null;
    }
    let config: LearnCertDisableConfig = this.getDict(key, LearnCertDisableConfig);
    if (!config) {
      let observable;
      if (param.isDept()) {
        observable = this.learnCertInterface.getLearnCertDisableConfigByDeptId(param.searchId);
      } else if (param.isGroup()) {
        observable = this.learnCertInterface.getLearnCertDisableConfigByGroupId(param.searchId);
      }
      return observable
        ? observable.pipe(
            map((resp: LearnCertDisableConfig) => {
              this.ls.set(key, new DictData(resp));
              return resp;
            })
          )
        : null;
    }
    if (config.tenantId === this.userService.getTenantId()) {
      return Observable.create(observer => {
        observer.next(config);
        observer.complete();
      });
    }
    this.ls.del(key);
    return this.getLearnCertDisableConfigDict(key, param);
  }

  private getTenantDepts(key: string, param?: DictDataParam): Observable<Dept[]> {
    let depts = this.getDict(key, Dept);
    if (!depts || depts.length === 0) {
      let observable = this.deptInterface.getDeptsByTenantId(param.searchId);
      return observable.pipe(
        map((resp: Dept[]) => {
          if (resp && resp.length !== 0) {
            this.ls.set(key, new DictData(resp));
          }
          return resp;
        })
      );
    }
    if (depts[0].tenantId === this.userService.getTenantId()) {
      return Observable.create(observer => {
        observer.next(depts);
        observer.complete();
      });
    }
    this.ls.del(key);
    return this.getTenantDepts(key, param);
  }

  /**
   * è·åè¢«å®ä¹ä¸ºç®¡çåçç§å®¤list
   */
  private getDefinedDepts(key: string, param?: DictDataParam): Observable<Dept[]> {
    let depts = this.getDict(key, Dept);
    if (!depts || depts.length === 0) {
      let observable = this.deptInterface.getDefinedDeptsByUserId(param.searchId);
      return observable.pipe(
        map((resp: Dept[]) => {
          if (resp && resp.length !== 0) {
            this.ls.set(key, new DictData(resp));
          }
          return resp;
        })
      );
    }
    if (depts[0].tenantId === this.userService.getTenantId()) {
      return Observable.create(observer => {
        observer.next(depts);
        observer.complete();
      });
    }
    this.ls.del(key);
    return this.getDefinedDepts(key, param);
  }
}
